import type { FC, PropsWithChildren } from 'react'
import { useSelector } from 'react-redux'

import { Avatar } from '@mui/material'

import { getUserInitials, getUserName, type NamedUser } from '~/shared/lib/names'
import ArrowTooltip from './ArrowTooltip'
import { AVATAR_BASE_SX, AVATAR_OWNER_SX, AVATAR_SIZE_SX } from './avatarSx'

interface UserAvatarProps {
  id: number
  size?: 'small' | 'large'
  src?: string
  /** Override the hover tooltip (defaults to the user's name). */
  tooltip?: string
  /**
   * Rendered when `id` isn't in the Redux `users.byId` store — which is the case
   * on the `/people/:id` person route, since it never dispatches the person into
   * that store.
   */
  fallbackUser?: NamedUser

  // `AVATAR_BASE_SX` hardcodes a 1rem margin that callers rely on; this lets one override it.
  avatarStyle?: Pick<React.CSSProperties, 'margin' | 'display' | 'order'>
}

export const UserAvatar: FC<PropsWithChildren<UserAvatarProps>> = props => {
  const { id, size = 'large', src, avatarStyle, tooltip, fallbackUser } = props

  const me = useSelector(state => state.me)
  const storeUser = useSelector(state => {
    return state.users.byId[id]
  })

  const user = storeUser ?? fallbackUser
  if (!user) {
    return null
  }

  const userInitials = getUserInitials(user)
  const userName = getUserName(user)

  // `users.byId` stores either a `UserRetrieveResponse` (patients fetched via
  // `getPatientSuccess`) or a `Provider` (provider list dispatched via
  // `GET_PROVIDERS_SUCCESS`); both are snake_case and expose `is_provider` /
  // `is_patient`.
  // The fallbackUser is a bare NamedUser without is_provider / is_patient, so
  // guard those reads; it renders with the neutral `other` style.
  let userType: keyof typeof AVATAR_OWNER_SX = 'other'
  if (id === me.id) {
    userType = 'me'
  } else if ('is_provider' in user && user.is_provider) {
    userType = 'provider'
  } else if ('is_patient' in user && user.is_patient) {
    userType = 'patient'
  }

  return (
    <ArrowTooltip title={tooltip ?? userName}>
      <Avatar
        sx={[AVATAR_BASE_SX, AVATAR_SIZE_SX[size], AVATAR_OWNER_SX[userType]]}
        style={avatarStyle}
        src={src}
      >
        {userInitials}
      </Avatar>
    </ArrowTooltip>
  )
}
